import { useState, useCallback, useEffect } from 'react'
import { toast } from 'sonner'
import { useAuth0 } from '@/lib/auth'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined

const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

type PushState = 'unsupported' | 'prompt' | 'granted' | 'denied' | 'subscribed'

/**
 * Marca de que la persona desactivó a propósito en este dispositivo.
 *
 * El re-suscribir automático de abajo existe para recuperar una suscripción que el navegador
 * tiró solo (rotación de claves, actualización del service worker, limpieza de datos). Sin esta
 * marca no distingue ese caso de una baja deliberada, y volvía a suscribir al recargar: el botón
 * de desactivar no duraba nada.
 */
/**
 * La clave lleva el usuario y no acepta ausencia: escribir bajo un usuario provisorio guarda la
 * baja en un lugar donde nadie la busca después, y el re-suscribir automático la ignora.
 */
const optOutKey = (userSub: string) => `brainhub-push-opt-out:${userSub}`

const tieneOptOut = (userSub: string): boolean => {
  try {
    return localStorage.getItem(optOutKey(userSub)) === 'true'
  } catch {
    return false
  }
}

const marcarOptOut = (userSub: string, valor: boolean) => {
  try {
    if (valor) localStorage.setItem(optOutKey(userSub), 'true')
    else localStorage.removeItem(optOutKey(userSub))
  } catch {
    /* sin storage, el auto-re-subscribe vuelve a su comportamiento viejo */
  }
}

export const usePushSubscription = () => {
  const { user, getAccessTokenSilently } = useAuth0()
  const { organizacionId } = useOrganizacion()
  const [state, setState] = useState<PushState>('unsupported')
  const [isLoading, setIsLoading] = useState(false)

  // Check current permission and subscription state.
  // If permission is granted but subscription is missing (e.g. after VAPID key
  // change or SW update), auto-re-subscribe to restore push seamlessly.
  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !VAPID_PUBLIC_KEY) {
      setState('unsupported')
      return
    }

    let mounted = true
    const permission = Notification.permission
    if (permission === 'denied') {
      setState('denied')
      return
    }

    navigator.serviceWorker.ready
      .then(async (registration) => {
        const subscription = await registration.pushManager.getSubscription()

        if (!mounted) return

        if (subscription) {
          setState('subscribed')
          return
        }

        // No active subscription. If user previously granted permission,
        // auto-re-subscribe transparently (covers VAPID key rotation, SW
        // update, browser storage cleanup, etc.). If permission is still
        // 'default', let user click "Activar push" manually.
        if (permission === 'granted' && user?.sub && !tieneOptOut(user.sub) && organizacionId) {
          try {
            const newSub = await registration.pushManager.subscribe({
              userVisibleOnly: true,
              applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY).buffer as ArrayBuffer,
            })

            const subJson = newSub.toJSON()
            const token = await getAccessTokenSilently()

            const response = await fetch('/api/push-subscribe', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({
                subscription: {
                  endpoint: subJson.endpoint,
                  keys: subJson.keys,
                },
                organizacionId,
                deviceInfo: {
                  userAgent: navigator.userAgent.substring(0, 200),
                  platform: navigator.platform.substring(0, 50),
                  language: navigator.language.substring(0, 20),
                },
              }),
            })

            if (response.ok && mounted) {
              setState('subscribed')
            } else if (mounted) {
              setState('granted')
            }
          } catch (err) {
            console.error('[push] auto-re-subscribe failed:', err)
            if (mounted) setState('granted')
          }
        } else {
          setState(permission === 'granted' ? 'granted' : 'prompt')
        }
      })
      .catch(() => {
        if (mounted) setState('unsupported')
      })

    return () => { mounted = false }
  }, [user?.sub, organizacionId, getAccessTokenSilently])

  const subscribe = useCallback(async () => {
    const userSub = user?.sub
    if (!VAPID_PUBLIC_KEY || !userSub || !organizacionId) return false

    setIsLoading(true)
    try {
      const permission = await Notification.requestPermission()
      // Cerrar el cartel del navegador sin elegir devuelve 'default', que NO es un rechazo:
      // tratarlo como tal decía "las bloqueaste" y escondía el botón de activar para siempre.
      if (permission === 'denied') {
        setState('denied')
        return false
      }
      if (permission !== 'granted') {
        setState('prompt')
        return false
      }

      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY).buffer as ArrayBuffer,
      })

      const subscriptionJson = subscription.toJSON()

      // Get Auth0 token for authenticated request
      const token = await getAccessTokenSilently()

      // Send to backend — userId extracted from JWT server-side
      const response = await fetch('/api/push-subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          subscription: {
            endpoint: subscriptionJson.endpoint,
            keys: subscriptionJson.keys,
          },
          organizacionId,
          deviceInfo: {
            userAgent: navigator.userAgent.substring(0, 200),
            platform: navigator.platform.substring(0, 50),
            language: navigator.language.substring(0, 20),
          },
        }),
      })

      if (!response.ok) throw new Error('Failed to save subscription')

      marcarOptOut(userSub, false)
      setState('subscribed')
      return true
    } catch (error) {
      console.error('Error subscribing to push:', error)
      // Deshacer la suscripción del navegador: si queda viva sin registro en el servidor, al
      // volver a entrar se lee como activada y la pantalla dice que llegan avisos que no llegan.
      try {
        const registration = await navigator.serviceWorker.ready
        const huerfana = await registration.pushManager.getSubscription()
        await huerfana?.unsubscribe()
      } catch {
        /* nada que revertir */
      }
      setState(Notification.permission === 'granted' ? 'granted' : 'prompt')
      return false
    } finally {
      setIsLoading(false)
    }
  }, [user?.sub, organizacionId, getAccessTokenSilently])

  const unsubscribe = useCallback(async () => {
    // `ProtectedRoute` solo mira `isLoading` e `isAuthenticated`, así que el componente puede
    // montar con el usuario todavía sin resolver. Desactivar en ese momento guardaría la baja
    // bajo una clave que después nadie lee, y el dispositivo volvería a suscribirse solo.
    const userSub = user?.sub
    if (!userSub) {
      toast.error('Todavía se está cargando tu usuario. Probá de nuevo en unos segundos.')
      return false
    }

    setIsLoading(true)
    marcarOptOut(userSub, true)
    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()

      if (subscription) {
        await subscription.unsubscribe()

        const token = await getAccessTokenSilently()
        await fetch('/api/push-subscribe', {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ endpoint: subscription.endpoint }),
        })
      }

      setState('prompt')
      return true
    } catch (error) {
      console.error('Error unsubscribing:', error)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [getAccessTokenSilently, user?.sub])

  return {
    state,
    isSupported: state !== 'unsupported',
    isSubscribed: state === 'subscribed',
    canSubscribe: state === 'prompt' || state === 'granted',
    isDenied: state === 'denied',
    isLoading,
    subscribe,
    unsubscribe,
  }
}
